import React from 'react';
import { MeatballMenu } from 'ui';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { SelectSingle } from 'app/area/shared/translated-components';
import { endpoints } from '../endpoints';
import { AccessPolicyItemProps } from '../types';
import { ExternalUrlFormIds } from 'app/area/external-url-groups/form/constants';
import { AccessPolicyValue } from 'app/area/external-url-groups/form/types-store';
import { getOnlyExistedValues } from 'app/area/external-url-groups/form/utils/helpers';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { AccessPolicyApplication } from 'app/area/external-url-groups/form/components/access-policy-application';
import styles from './access-policy-item.scss';

export const AccessPolicyItem: React.FC<AccessPolicyItemProps> = ({
  item, options, accessPolicyValidation, validation, translation, removeAllAccessPolicy, updateAccessPolicyItem,
  resetAccessPolicyClientValidation, resetAccessPolicyServerValidation, loadOptions, removeAccessPolicy
}) => {

  const accessPolicyId = item?.id;

  const resetValidationByKey = (key: keyof AccessPolicyValue) => {
    resetAccessPolicyClientValidation(accessPolicyId, key);
    resetAccessPolicyServerValidation(accessPolicyId);
  };

  const onCaseTypeChange = (caseTypeValue: ComplexValue<number>) => {
    const caseType = getOnlyExistedValues(caseTypeValue);

    resetValidationByKey(ExternalUrlFormIds.caseType);
    updateAccessPolicyItem({
      ...item,
      caseType,
      operation: undefined
    });
  };

  const onOperationChange = (operation: ComplexValue<number>) => {
    resetValidationByKey(ExternalUrlFormIds.operation);
    updateAccessPolicyItem({ ...item, operation: getOnlyExistedValues(operation) });
  };

  const onCaseTypeLoadOptions = (id: string, loadOptionsParams: LoadOptions) => {
    loadOptions(ExternalUrlFormIds.caseType, endpoints.caseTypeOptions, loadOptionsParams);
  };

  const onOperationLoadOptions = (loadOptionsParams: LoadOptions) => {
    loadOptions(ExternalUrlFormIds.operation,
      endpoints.operationOptions(String(item.caseType.value)), loadOptionsParams);
  };

  const onRemove = () => {
    resetAccessPolicyClientValidation(accessPolicyId, ExternalUrlFormIds.caseType);
    resetAccessPolicyClientValidation(accessPolicyId, ExternalUrlFormIds.operation);
    resetAccessPolicyServerValidation(accessPolicyId);
    removeAccessPolicy(accessPolicyId);
  };

  const accessPolicyItemValidation = accessPolicyValidation?.[item.id]
    ?.caseType?.messages?.find(message => message?.parameters?.caseType === item.caseType.value
      && message?.parameters?.operation === item.operation.value
    );

  const caseTypeValidation = validation?.caseType?.messages?.some(message => message.parameters?.id === item.id)
    ? validation?.caseType
    : undefined;

  const operationValidation = validation?.operation?.messages?.some(message => message.parameters?.id === item.id)
    ? validation?.operation
    : undefined;

  const hasErrors = !!accessPolicyItemValidation || !!caseTypeValidation || !!operationValidation;

  return <div className="row">
    <div className="col-md-12" >
      <div
        className={`${styles.container} ${hasErrors ? styles.hasError : ''}`}>
        <MeatballMenu closeAfterClick={true}>
          <li>
            <button type="button" className="dropdown-item" onClick={onRemove}>
              {translation.remove}
            </button>
          </li>
          <li>
            <button type="button" className="dropdown-item" onClick={removeAllAccessPolicy}>
              {translation.remove_all}
            </button>
          </li>
        </MeatballMenu>
        <div className="row mt-3">
          <AccessPolicyApplication
            className={styles.selectCaseType}
            id={ExternalUrlFormIds.caseType}
            label={translation.application_label}
            size={6}
            valueItem={item?.caseType}
            required={true}
            options={options?.caseType?.codes as ComplexValue<number>[] || []}
            fetchingOptions={options?.caseType?.fetching}
            translation={translation}
            onLoadOptions={onCaseTypeLoadOptions}
            onChange={onCaseTypeChange}
            validation={accessPolicyValidation[item.id]?.caseType || caseTypeValidation} />
          <div className="col-lg-6 col-sm-12">
            <SelectSingle
              componentId={ExternalUrlFormIds.operation}
              className={undefined}
              readOnly={!isDefinedIncludingZero(item?.caseType?.value)}
              label={{ text: translation.operation_label, required: true }}
              valueItem={item?.operation}
              onChange={onOperationChange}
              placeholder={translation?.search}
              options={options?.operation?.codes as ComplexValue<number>[] || []}
              fetching={options?.operation?.fetching}
              info={{ visible: true, text: translation.access_policy_operation_info }}
              validation={operationValidation}
              onLoadOptions={onOperationLoadOptions} />
          </div>
        </div>
      </div>
    </div>
  </div>;
};
